import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import "../components/EmployeeTime.css";

const MESSAGES = {
  LOGIN_EXPIRED: "ログインの有効期限が切れました。再度ログインしてください。",
  ERROR_GENERIC: "エラーが発生しました",
};

/** ISO datetime または "HH:MM" 文字列を "HH:MM" にフォーマット */
function fmtTime(val) {
  if (!val) return "—";
  // ISO文字列の場合
  if (val.includes("T") || val.includes("+") || val.includes("Z")) {
    const d = new Date(val);
    if (isNaN(d.getTime())) return "—";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  // すでに "HH:MM" 形式
  return val;
}

function ymdLocal(y, m0, d) {
  const m = String(m0 + 1).padStart(2, "0");
  const day = String(d).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 表示対象の14ヶ月分（当月＋過去13ヶ月）を新しい順で生成 */
const MONTHS_TO_SHOW = 14;
function buildMonthOptions() {
  const now = new Date();
  const baseY = now.getFullYear();
  const baseM = now.getMonth(); // 0-11
  const list = [];
  for (let i = 0; i < MONTHS_TO_SHOW; i += 1) {
    const m0 = baseM - i;
    const y = baseY + Math.floor(m0 / 12);
    const m = ((m0 % 12) + 12) % 12;
    const isCurrent = i === 0;
    const lastDay = new Date(y, m + 1, 0).getDate();
    list.push({
      key: `${y}-${String(m + 1).padStart(2, "0")}`,
      year: y,
      month: m + 1,
      isCurrent,
      labelJa: `${y}年${m + 1}月${isCurrent ? "（当月）" : ""}`,
      // start/end は API が year/month から自動算出するが、当月表示の period_note 用にも残す
      start: ymdLocal(y, m, 1),
      end: isCurrent ? ymdLocal(y, m, now.getDate()) : ymdLocal(y, m, lastDay),
    });
  }
  return list;
}

function findMonthOption(options, key) {
  return options.find((o) => o.key === key) || options[0];
}

const EmployeeTimeRecord = () => {
  const [message, setMessage] = useState("");
  const [actualsLoading, setActualsLoading] = useState(true);
  const [actualsPayload, setActualsPayload] = useState(null);
  const [actualsEdit, setActualsEdit] = useState(null);
  const [editMode, setEditMode] = useState("schedule");
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [actualsSaving, setActualsSaving] = useState(false);

  // 14ヶ月分の月オプション（当月含む）
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const defaultKey = monthOptions[0]?.key || "";
  const [actualsMonthKey, setActualsMonthKey] = useState(defaultKey);
  const actualsMonthKeyRef = useRef(defaultKey);

  const loadActualsRecords = useCallback(async (monthKey) => {
    const opt = findMonthOption(monthOptions, monthKey);
    if (!opt) return;
    // year/month を渡すことで、過去月はバックエンドのスナップショットを利用
    const res = await api.get("/worktime/my/records/", {
      params: { year: opt.year, month: opt.month },
    });
    setActualsPayload(res.data);
  }, [monthOptions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setActualsLoading(true);
      try {
        await loadActualsRecords(defaultKey);
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 401) {
            setMessage(MESSAGES.LOGIN_EXPIRED);
          } else if (err.response?.status === 403) {
            setMessage(err.response?.data?.detail || "この画面は管理者アカウントでは利用できません。");
          } else {
            setMessage(err.response?.data?.message || MESSAGES.ERROR_GENERIC);
          }
        }
      } finally {
        if (!cancelled) setActualsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadActualsRecords, defaultKey]);

  // 画面を離れて戻ってきたとき（バックグラウンド復帰・タブ切替）に最新データを再取得
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        loadActualsRecords(actualsMonthKeyRef.current).catch(() => {});
      }
    };
    const handlePageShow = (e) => {
      if (e.persisted) {
        loadActualsRecords(actualsMonthKeyRef.current).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [loadActualsRecords]);

  const handleActualsMonthChange = async (key) => {
    if (key === actualsMonthKey) return;
    setActualsMonthKey(key);
    actualsMonthKeyRef.current = key;
    setActualsEdit(null);
    setActualsLoading(true);
    try {
      await loadActualsRecords(key);
    } catch (err) {
      setMessage(err.response?.data?.message || MESSAGES.ERROR_GENERIC);
    } finally {
      setActualsLoading(false);
    }
  };

  const currentMonthOption = findMonthOption(monthOptions, actualsMonthKey);
  const isPastMonth = !!(currentMonthOption && !currentMonthOption.isCurrent);

  /** 表示中の期間の総合労働時間の合計。
   *  計(h) は「スケジュール時間 ＋ 残業 − 遅刻 − 早退」で算出済みの値なので、
   *  これを合計すれば残業・遅刻・早退を反映した総合労働時間になる。
   *  表の列は各日を小数2桁で表示しているため、合計も同じ丸め方をした値を足す
   *  （丸める前の値で足すと、表示されている数字の合計と一致しないことがある）。 */
  const periodTotalHours = useMemo(() => {
    const list = actualsPayload?.records || [];
    const total = list.reduce(
      (sum, rec) => sum + Math.round((Number(rec.total_hours) || 0) * 100),
      0
    );
    return total / 100;
  }, [actualsPayload]);

  const openActualsEditRow = (rec) => {
    if (!rec.can_self_correct) return;
    setActualsEdit(rec);
    setEditMode("actual"); // デフォルトは「実際の出勤時刻」で時刻入力欄を即表示
    const toHhMm = (val) => {
      if (!val) return "";
      const t = fmtTime(val);
      return t === "—" ? "" : t;
    };
    setEditStart(toHhMm(rec.start_time));
    setEditEnd(toHhMm(rec.end_time));
  };

  const submitActualsEdit = async () => {
    if (!actualsEdit) return;
    setActualsSaving(true);
    let saveOk = false;
    try {
      const modeLabel = editMode === "schedule" ? "定入力" : "実際の出勤時刻";
      const body = {
        date: (actualsEdit.date || "").slice(0, 10),
        mode: modeLabel,
        start_time: editStart || null,
        end_time: editEnd || null,
      };
      await api.post("/worktime/self_correct/", body);
      saveOk = true;
      setMessage("勤怠を修正しました。");
      setActualsEdit(null);
    } catch (err) {
      setMessage(err.response?.data?.message || MESSAGES.ERROR_GENERIC);
    } finally {
      setActualsSaving(false);
    }
    if (!saveOk) return;
    try {
      window.dispatchEvent(new CustomEvent("worktime-punch-state-changed"));
    } catch (_) { /* ignore */ }
    // 保存成功後: ローディング状態でテーブルを再取得（handleActualsMonthChange と同パターン）
    setActualsLoading(true);
    try {
      await loadActualsRecords(actualsMonthKey);
    } catch (_) {
      // リロード失敗はサイレント（保存自体は成功しているため）
    } finally {
      setActualsLoading(false);
    }
  };

  return (
    <div className="employee-time-record-page">
      <header className="employee-time-record-header">
        <h1 className="employee-time-record-title">実績</h1>
        <div className="employee-time-record-header-actions">
          <Link to="/card_front" className="employee-time-record-back-link">
            打刻画面へ戻る
          </Link>
        </div>
      </header>

      {message && <p className="employee-time-record-message">{message}</p>}

      <div className="employee-time-actuals-modal employee-time-record-card">
        <div className="employee-time-actuals-month-select-wrap">
          <label htmlFor="actuals-month-select" className="employee-time-actuals-month-label">
            表示する月
          </label>
          <select
            id="actuals-month-select"
            className="employee-time-actuals-month-select"
            value={actualsMonthKey}
            onChange={(e) => handleActualsMonthChange(e.target.value)}
            disabled={actualsLoading}
          >
            {monthOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.labelJa}
              </option>
            ))}
          </select>
          {isPastMonth && (
            <span
              className="employee-time-actuals-from-snapshot-badge"
              title="過去月は確定データを表示しています"
            >
              確定データ
            </span>
          )}
        </div>
        {!actualsLoading && (actualsPayload?.period_note || currentMonthOption) && (
          <p className="employee-time-actuals-note">
            {actualsPayload?.period_note}
            {currentMonthOption && (
              <span className="employee-time-actuals-period-range">
                表示期間: {currentMonthOption.start} ～ {currentMonthOption.end}
              </span>
            )}
            {/* 表示している期間の労働時間合計（表の「計(h)」列の合計） */}
            <span className="employee-time-actuals-period-total">
              合計: <strong>{periodTotalHours.toFixed(2)}</strong> 時間
            </span>
          </p>
        )}
        {actualsLoading ? (
          <p className="employee-time-actuals-loading">読み込み中...</p>
        ) : (
          <div className="employee-time-actuals-table-wrap">
            <table className="employee-time-actuals-table">
              <thead>
                <tr>
                  <th scope="col">日付</th>
                  <th scope="col">勤務</th>
                  <th scope="col">出社</th>
                  <th scope="col">退社</th>
                  <th scope="col" className="employee-time-actuals-col-hours">
                    計(h)
                  </th>
                </tr>
              </thead>
              <tbody>
                {(actualsPayload?.records || []).map((rec) => {
                  const shortDate = (rec.date || "").slice(0, 10);
                  const inT = fmtTime(rec.start_time);
                  const outT = fmtTime(rec.end_time);
                  const clickable = Boolean(rec.can_self_correct);

                  // 勤務日かどうか（シフト割当あり or 何らかの打刻あり）
                  const hasSchedule = !!(rec.schedule_start);
                  const hasAnyPunch = !!(rec.start_time || rec.end_time);
                  const isWorkDay = hasSchedule || hasAnyPunch;

                  // 打刻の有無
                  const hasBothPunches = !!(rec.start_time && rec.end_time);
                  const startMissing = isWorkDay && !rec.start_time;
                  const endMissing = isWorkDay && !rec.end_time;
                  const isMissed = isWorkDay && !hasBothPunches;
                  const isComplete = isWorkDay && hasBothPunches;

                  const isEditing = actualsEdit?.date === rec.date;

                  // 日付ボタンのクラス
                  const dateBtnClass = [
                    "employee-time-actuals-date-btn",
                    clickable ? "employee-time-actuals-date-btn-clickable" : "",
                    isMissed ? "employee-time-actuals-date-btn-missed" : "",
                    isComplete ? "employee-time-actuals-date-btn-complete" : "",
                  ].filter(Boolean).join(" ");

                  return (
                    <tr
                      key={rec.date}
                      className={`employee-time-actuals-row${isMissed ? " employee-time-actuals-row-missed" : ""}`}
                      style={isEditing ? { outline: "2px solid #f59e0b" } : undefined}
                    >
                      <td>
                        <button
                          type="button"
                          className={dateBtnClass}
                          onClick={() => openActualsEditRow(rec)}
                          disabled={!clickable}
                        >
                          {shortDate}（{rec.day_of_week || ""}）
                        </button>
                      </td>
                      <td className="employee-time-actuals-work">{rec.work_label || ""}</td>
                      <td>
                        {/* 表は表示のみ。修正は下の「修正 —」パネルで行う */}
                        <span className={startMissing ? "employee-time-actuals-missing" : undefined}>{inT}</span>
                      </td>
                      <td>
                        <span className={endMissing ? "employee-time-actuals-missing" : undefined}>{outT}</span>
                      </td>
                      <td className="employee-time-actuals-col-hours">{Number(rec.total_hours ?? 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {actualsPayload?.records?.length === 0 && (
              <p className="employee-time-actuals-empty">データがありません。</p>
            )}
          </div>
        )}

        {actualsEdit && (() => {
          const hasBoth = !!(actualsEdit.start_time && actualsEdit.end_time);
          const startMissing = !actualsEdit.start_time;
          const endMissing = !actualsEdit.end_time;

          // スケジュール終了時刻を現在時刻と比較
          const now = new Date();
          const nowMins = now.getHours() * 60 + now.getMinutes();
          const schedEndMins = actualsEdit.schedule_end
            ? (() => {
                const [hh, mm] = actualsEdit.schedule_end.split(":").map(Number);
                return hh * 60 + mm;
              })()
            : null;
          // スケジュール終了時刻を過ぎているか（スケジュールなしの場合は「過ぎた」扱い）
          const isPastScheduleEnd = schedEndMins !== null ? nowMins >= schedEndMins : true;

          // 修正パネルで出勤・退勤どちらの入力欄を表示するか
          // hasBoth (両方打刻済み) → 両方表示（通常の修正）
          // startMissing && endMissing:
          //   スケジュール終了前 → 出勤のみ表示
          //   スケジュール終了後 → 両方表示
          // startMissing && !endMissing → 出勤のみ表示
          // !startMissing && endMissing:
          //   スケジュール終了前 → 退勤のみ表示
          //   スケジュール終了後 → 両方表示
          let showStartInput, showEndInput;
          if (hasBoth) {
            showStartInput = true;
            showEndInput = true;
          } else if (startMissing && endMissing) {
            showStartInput = true;
            showEndInput = isPastScheduleEnd;
          } else if (startMissing && !endMissing) {
            showStartInput = true;
            showEndInput = false;
          } else {
            // !startMissing && endMissing（出勤済み・退勤のみ未打刻）
            // → 退勤のみ表示（例: 前日の退勤打刻忘れ）
            showStartInput = false;
            showEndInput = true;
          }

          const showBothInputs = showStartInput && showEndInput;
          const scheduleInfo = actualsEdit.schedule_start
            ? `スケジュール: ${actualsEdit.schedule_start}〜${actualsEdit.schedule_end || "?"}`
            : "";

          // 未打刻のラベル
          const missingLabel = startMissing && endMissing
            ? "出勤・退勤 未打刻"
            : startMissing
            ? "出勤 未打刻"
            : endMissing
            ? "退勤 未打刻"
            : "";

          return (
            <div className="employee-time-record-edit-panel">
              <p className="employee-time-record-edit-title">
                修正 — {(actualsEdit.date || "").slice(0, 10)}（{actualsEdit.day_of_week}）
                {missingLabel && (
                  <span style={{ fontSize: "0.85em", color: "#dc2626", marginLeft: "8px" }}>
                    {missingLabel}
                  </span>
                )}
                {scheduleInfo && (
                  <span style={{ fontSize: "0.8em", color: "#6b7280", marginLeft: "8px" }}>
                    {scheduleInfo}
                  </span>
                )}
              </p>
              {showBothInputs && (
                <div className="employee-time-record-edit-radios">
                  <label>
                    <input
                      type="radio"
                      name="editMode"
                      checked={editMode === "schedule"}
                      onChange={() => setEditMode("schedule")}
                    />
                    <span>スケジュール入力（シフトの開始・終了時刻で登録）</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="editMode"
                      checked={editMode === "actual"}
                      onChange={() => setEditMode("actual")}
                    />
                    <span>実際の出勤時刻</span>
                  </label>
                </div>
              )}
              <div className="employee-time-record-edit-times">
                {showStartInput && (
                  <label>
                    出勤
                    <input
                      type="time"
                      value={editStart}
                      onChange={(e) => setEditStart(e.target.value)}
                    />
                  </label>
                )}
                {showEndInput && (
                  <label>
                    退勤
                    <input
                      type="time"
                      value={editEnd}
                      onChange={(e) => setEditEnd(e.target.value)}
                    />
                  </label>
                )}
              </div>
              <div className="employee-time-record-edit-actions">
                <button
                  type="button"
                  className="employee-time-overtime-btn employee-time-overtime-btn-overtime"
                  onClick={submitActualsEdit}
                  disabled={actualsSaving}
                >
                  {actualsSaving ? "送信中..." : "修正"}
                </button>
                <button
                  type="button"
                  className="employee-time-overtime-btn employee-time-overtime-btn-private"
                  onClick={() => setActualsEdit(null)}
                  disabled={actualsSaving}
                >
                  キャンセル
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default EmployeeTimeRecord;
